import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, Menu, X, ShieldCheck, MessageCircle, Home, Search as SearchIcon, Package, Mail } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { waEnquiryUrl } from "../../lib/contact";
import SearchOverlay from "../SearchOverlay";

const LOGO_URL = "/logo.png";

// Same press-effect classes for all 4 top-bar buttons.
// - Idle: brand-dark icon
// - Press (touch + mouse-down): logo-red bg + white icon (via :active)
// - On release / hover-out: instantly back to idle. NO sticky red.
const PRESS_BTN = "p-2.5 rounded-full text-brand-dark transition-colors duration-150 " +
  "active:bg-brand-chili active:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-chili/40";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Initialise from current scrollY synchronously so back-navigation lands
  // with the correct compact/full header — no resize transition on mount.
  const [scrolled, setScrolled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.scrollY > 16; // mid-point between ENTER and EXIT
  });
  // First-paint guard: while true, the height/logo transitions are disabled
  // so any post-mount scroll-event recalculation lands instantly.
  const [enableTransitions, setEnableTransitions] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEnableTransitions(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // ALSO suppress transitions for one frame on every route change. This is
  // what kills the back-nav stutter: when the user clicks Back from a
  // product page to /shop, ScrollManager restores scrollY synchronously and
  // the scroll handler immediately sets `scrolled=true` — but without this
  // guard, the inner header would animate from expanded (92px) → compact
  // (68px) over 500ms, causing the visible "shrinking" effect.
  const loc = useLocation();
  useEffect(() => {
    setEnableTransitions(false);
    // Snap the scrolled-state to whatever the current scroll position is so
    // the new page lands with the correct compact/full header on the very
    // first paint.
    setScrolled(window.scrollY > 16);
    const id = requestAnimationFrame(() => {
      // Re-snap after the next paint in case ScrollManager restored scrollY
      // between location change and the first paint.
      setScrolled((prev) => (prev ? window.scrollY > 8 : window.scrollY > 32));
      requestAnimationFrame(() => setEnableTransitions(true));
    });
    return () => cancelAnimationFrame(id);
  }, [loc.pathname, loc.search]);

  useEffect(() => {
    let ticking = false;
    const ENTER = 32;  // scrollY threshold to become "scrolled"
    const EXIT  = 8;   // scrollY threshold to revert (hysteresis prevents flicker)
    const tick = () => {
      const y = window.scrollY;
      setScrolled((prev) => (prev ? y > EXIT : y > ENTER));
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) { ticking = true; requestAnimationFrame(tick); }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  // Home: always scroll-to-top. Robust against any errors.
  const handleHomeClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
        requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
      }
    } catch {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, navigate]);

  return (
    <>
      <header
        className={`sticky top-0 z-50 glass-header border-b transition-[border-color,box-shadow] duration-500 ${scrolled ? "border-brand-line shadow-sm" : "border-transparent"}`}
        data-testid="site-header"
      >
        <div
          className={`container-x flex items-center justify-between gap-3 ${enableTransitions ? "transition-[height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" : ""} ${
            scrolled ? "h-[68px] lg:h-[78px]" : "h-[92px] lg:h-[128px]"
          }`}
        >
          {/* LEFT — Home + Logo */}
          <div className="flex items-center gap-3 sm:gap-5">
            <button
              type="button"
              onClick={handleHomeClick}
              className={PRESS_BTN + " bg-brand-subtle w-10 h-10 sm:w-11 sm:h-11 inline-flex items-center justify-center !p-0"}
              aria-label="Home — scroll to top"
              data-testid="nav-home-btn"
            >
              <Home className="w-[18px] h-[18px]" strokeWidth={1.8} />
            </button>

            <div className="flex items-center select-none" aria-label="Sri Durga Foods" data-testid="brand-logo">
              <img
                src={LOGO_URL}
                alt="Sri Durga Foods"
                draggable="false"
                className={`w-auto ${enableTransitions ? "transition-[height] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" : ""} will-change-[height] ${
                  scrolled ? "h-12 sm:h-14 lg:h-16" : "h-16 sm:h-24 lg:h-28"
                }`}
              />
            </div>
          </div>

          {/* RIGHT — Search + Cart + Menu */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className={PRESS_BTN}
              aria-label="Search"
              data-testid="nav-search-btn"
            >
              <SearchIcon className="w-[20px] h-[20px]" strokeWidth={1.7} />
            </button>

            <button
              type="button"
              onClick={() => navigate("/cart")}
              className={`relative ${PRESS_BTN}`}
              aria-label="Cart"
              data-testid="nav-cart"
            >
              <ShoppingBag className="w-[20px] h-[20px]" strokeWidth={1.5} />
              {count > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 bg-brand-chili text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className={PRESS_BTN}
              aria-label="Menu"
              data-testid="menu-toggle"
            >
              <Menu className="w-[20px] h-[20px]" strokeWidth={1.7} />
            </button>
          </div>
        </div>

        {/* MENU DRAWER — rendered via portal to escape header's containing block */}
        {menuOpen && createPortal(
          <>
            <div
              className="fixed inset-0 bg-brand-dark/40 backdrop-blur-sm z-[60] animate-fade-in"
              onClick={() => setMenuOpen(false)}
              data-testid="menu-backdrop"
            />
            <aside
              className="fixed top-0 right-0 h-screen w-[88%] max-w-sm bg-brand-cream z-[70] shadow-2xl flex flex-col animate-slide-in-right"
              data-testid="menu-drawer"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-brand-line">
                <span className="overline text-brand-chili">Menu</span>
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  className={PRESS_BTN}
                  aria-label="Close menu"
                  data-testid="menu-close"
                >
                  <X className="w-5 h-5" strokeWidth={1.7} />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-6 py-6">
                <ul className="space-y-0.5">
                  {user ? (
                    <li>
                      <Link
                        to="/account"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 font-serif text-2xl text-brand-dark py-4 border-b border-brand-line/60 transition-colors active:text-brand-chili"
                        data-testid="drawer-account"
                      >
                        <User className="w-5 h-5 text-brand-muted" strokeWidth={1.5} />
                        Account
                      </Link>
                    </li>
                  ) : (
                    <li>
                      <Link
                        to="/login"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 font-serif text-2xl text-brand-dark py-4 border-b border-brand-line/60 transition-colors active:text-brand-chili"
                        data-testid="drawer-login"
                      >
                        <User className="w-5 h-5 text-brand-muted" strokeWidth={1.5} />
                        Login
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link
                      to="/shop"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 font-serif text-2xl text-brand-dark py-4 border-b border-brand-line/60 transition-colors active:text-brand-chili"
                      data-testid="drawer-shop"
                    >
                      <ShoppingBag className="w-5 h-5 text-brand-muted" strokeWidth={1.5} />
                      Shop
                    </Link>
                  </li>
                  {user && (
                    <li>
                      <Link
                        to="/orders"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 font-serif text-2xl text-brand-dark py-4 border-b border-brand-line/60 transition-colors active:text-brand-chili"
                        data-testid="drawer-orders"
                      >
                        <Package className="w-5 h-5 text-brand-muted" strokeWidth={1.5} />
                        Your Orders
                      </Link>
                    </li>
                  )}
                  {/* iter67 — Contact Us mirrors the footer "Contact us" link
                       (same destination: /contact). Placed immediately after
                       "Your Orders" per the requested menu order. */}
                  <li>
                    <Link
                      to="/contact"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 font-serif text-2xl text-brand-dark py-4 border-b border-brand-line/60 transition-colors active:text-brand-chili"
                      data-testid="drawer-contact"
                    >
                      <Mail className="w-5 h-5 text-brand-muted" strokeWidth={1.5} />
                      Contact Us
                    </Link>
                  </li>
                  {["admin", "super_admin"].includes(user?.role) && (
                    <li>
                      <Link
                        to="/super-admin"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 font-serif text-xl text-brand-chili py-4 border-b border-brand-line/60 transition-colors active:opacity-80"
                        data-testid="drawer-admin"
                      >
                        <ShieldCheck className="w-5 h-5" strokeWidth={1.5} /> Admin dashboard
                      </Link>
                    </li>
                  )}
                  {user?.role === "employee_admin" && (
                    <li>
                      <Link
                        to="/admin-orders"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 font-serif text-xl text-brand-chili py-4 border-b border-brand-line/60 transition-colors active:opacity-80"
                        data-testid="drawer-check-orders"
                      >
                        <ShieldCheck className="w-5 h-5" strokeWidth={1.5} /> Check Orders
                      </Link>
                    </li>
                  )}
                </ul>

                <div className="mt-8 space-y-3">
                  <a
                    href={waEnquiryUrl()}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white py-3 rounded-full text-sm font-medium transition-transform active:scale-[0.98]"
                    data-testid="drawer-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={2} />
                    Chat on WhatsApp
                  </a>

                  {user && (
                    <button
                      type="button"
                      onClick={() => { logout(); setMenuOpen(false); navigate("/"); }}
                      className="hidden"
                      data-testid="drawer-logout"
                    />
                  )}
                </div>
              </nav>

              <div className="px-6 py-5 border-t border-brand-line text-[11px] uppercase tracking-[0.25em] text-brand-muted text-center">
                Nellore, Andhra · Since 2002
              </div>
            </aside>
          </>,
          document.body,
        )}
      </header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
